import { Button as BaseUIButton, type ButtonProps as BaseUIButtonProps } from "@base-ui/react/button";
import type { IconProps } from "@obs/icons";
import type { StyleXStyles } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";
import type { ReactElement, Ref } from "react";

import { Loader } from "../../Loader";
import { Typography } from "../../Typography";
import { buttonPrimitiveStyles } from "../button.stylex";
import { IconButtonPrimitive } from "./IconButtonPrimitive";

export type ButtonPrimitiveProps = {
	ref?: Ref<HTMLElement>;
	sx?: StyleXStyles;
	iconPosition?: "start" | "end";
	isBlock?: boolean;
	isLoading?: boolean;
	icon?: {
		Component: (props: IconProps) => ReactElement;
	} & Omit<IconProps, "size" | "iconColor">;
} & Omit<BaseUIButtonProps, "className" | "style">;

export function ButtonPrimitive({
	children,
	ref,
	sx,
	icon,
	iconPosition = "start",
	isBlock,
	isLoading,
	...props
}: ButtonPrimitiveProps) {
	const iconOnly = !children && Boolean(icon);

	const iconStart = !iconOnly && icon && iconPosition === "start";
	const iconEnd = !iconOnly && icon && iconPosition === "end";

	return (
		<BaseUIButton
			ref={ref}
			{...props}
			{...stylex.props(
				buttonPrimitiveStyles.base,
				iconOnly && buttonPrimitiveStyles.iconOnly,
				iconStart && buttonPrimitiveStyles.iconStart,
				iconEnd && buttonPrimitiveStyles.iconEnd,
				isBlock && buttonPrimitiveStyles.block,
				isLoading && buttonPrimitiveStyles.loading,
				sx,
			)}
		>
			{icon && iconPosition === "start" && <IconButtonPrimitive {...icon} isLoading={isLoading} />}
			{children && (
				<Typography.LabelDefault inheritColor {...stylex.props(isLoading && buttonPrimitiveStyles.contentHidden)}>
					{children}
				</Typography.LabelDefault>
			)}
			{icon && iconPosition === "end" && <IconButtonPrimitive {...icon} isLoading={isLoading} />}
			{isLoading && <Loader.Inherit sx={buttonPrimitiveStyles.loader} />}
		</BaseUIButton>
	);
}
